import { AlertCircle, KeyRound, ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { errorMessage } from "../../lib/error-message.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Button } from "../ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";

export const ChangePasswordForm = ({
  minLength = 8,
}: {
  minLength?: number;
}) => {
  const client = useUmClient();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [noPassword, setNoPassword] = useState(false);

  const mismatched = confirm.length > 0 && confirm !== next;
  const tooShort = next.length > 0 && next.length < minLength;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (mismatched || tooShort) return;
    setLoading(true);
    setError(null);
    setSuccess(false);
    const res = await client.changePassword({
      currentPassword: current,
      newPassword: next,
    });
    setLoading(false);
    if (res.ok) {
      setSuccess(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } else if (res.error.kind === "http" && res.error.code === "no_password_set") {
      // Social/passkey-only account — there is no password to change.
      setNoPassword(true);
    } else {
      setError(errorMessage(res.error, "Couldn't change password."));
    }
  };

  if (noPassword) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="size-4 text-muted-foreground" />
            Change password
          </CardTitle>
          <CardDescription>
            This account has no password — you sign in with a social provider
            or passkey. Ask an administrator to email you a set-password link
            if you'd like one.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="size-4 text-muted-foreground" />
          Change password
        </CardTitle>
        <CardDescription>
          Use a long, unique password. We'll keep you signed in on this device.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-4">
          <Field label="Current password" htmlFor="current-pw" required>
            <Input
              id="current-pw"
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </Field>
          <Field
            label="New password"
            htmlFor="next-pw"
            required
            description={`At least ${minLength} characters.`}
            error={tooShort ? `New password is too short.` : undefined}
          >
            <Input
              id="next-pw"
              type="password"
              autoComplete="new-password"
              required
              value={next}
              onChange={(e) => setNext(e.target.value)}
              aria-invalid={tooShort || undefined}
            />
          </Field>
          <Field
            label="Confirm new password"
            htmlFor="confirm-pw"
            required
            error={mismatched ? "Passwords don't match." : undefined}
          >
            <Input
              id="confirm-pw"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              aria-invalid={mismatched || undefined}
            />
          </Field>
          {success ? (
            <Alert variant="success">
              <ShieldCheck />
              <AlertDescription>Your password has been updated.</AlertDescription>
            </Alert>
          ) : null}
          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <div className="flex justify-end">
            <Button
              type="submit"
              loading={loading}
              disabled={mismatched || tooShort || !current || !next}
            >
              Update password
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
